import { hazardKeyLabel, type HazardKey } from "@/lib/results";
import type { ReportModel } from "@/lib/report";
import { HorizonLabel, SectionHeading } from "./ReportPage";

const hazardOrDash = (hazard: HazardKey | null) => (hazard ? hazardKeyLabel(hazard) : "—");

function Takeaway({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[11px] leading-snug text-muted-foreground">{label}</p>
      <p className="text-sm font-semibold">{value}</p>
    </div>
  );
}

/**
 * The four takeaways from functional spec §3 (Tab 2), one column per horizon.
 * Labels intentionally match TakeawayCards.tsx word for word.
 */
export function ReportTakeaways({ model }: { model: ReportModel }) {
  return (
    <section className="break-inside-avoid">
      <SectionHeading>Key takeaways</SectionHeading>
      <div className="mt-3 flex gap-4">
        {model.sections.map((s) => (
          <div key={s.horizon} className="flex min-w-0 flex-1 flex-col gap-3">
            {model.sections.length > 1 && <HorizonLabel>{s.label}</HorizonLabel>}
            <Takeaway label="Hazard with most assets at high risk" value={hazardOrDash(s.takeaways.mostAssetsAtRisk)} />
            <Takeaway label="Hazard riskiest for top-importance assets" value={hazardOrDash(s.takeaways.riskiestForTopAssets)} />
            <Takeaway
              label="Hazard riskiest for the most important value chain stage"
              value={hazardOrDash(s.takeaways.riskiestForTopValueChainStage)}
            />
            <div>
              <p className="text-[11px] leading-snug text-muted-foreground">% of assets at high risk, per hazard</p>
              <ul className="mt-1 space-y-0.5">
                {s.takeaways.percentAtHighRisk.map((p) => (
                  <li key={p.key} className="flex items-baseline justify-between gap-2 text-xs">
                    <span className="text-muted-foreground">{hazardKeyLabel(p.key)}</span>
                    <span className="font-semibold">{p.pct === null ? "—" : `${p.pct}%`}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
